import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { TASK_TYPES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Check, Lock, MapPin, Calendar as CalIcon, ChevronLeft, ChevronRight, UserCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "@/components/ConfirmDialog";

const TYPE_OPTIONS = ["chargement", "livraison", "montage", "preparation", "administratif", "reparation", "autre"];

export default function Planning() {
  const navigate = useNavigate();
  const [data, setData] = useState({ days: [], columns: {}, can_manage: false, members: [], me: null });
  const [open, setOpen] = useState(false);
  const [weekStart, setWeekStart] = useState(null);
  const [mine, setMine] = useState(false);
  const empty = { title: "", type: "chargement", date: new Date().toISOString().slice(0, 10), assignees: [], event_id: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [delTask, setDelTask] = useState(null);

  const load = async (ws) => { const { data } = await api.get("/planning", { params: ws ? { week_start: ws } : {} }); setData(data); setWeekStart(data.week_start); };
  useEffect(() => { load(); }, []);

  const canManage = data.can_manage;
  const dragId = useRef(null);
  const moveTask = async (date) => {
    const id = dragId.current; dragId.current = null;
    if (!canManage || !id) return;
    try { await api.patch(`/tasks/${id}/move`, { date }); load(weekStart); } catch { toast.error("Impossible de déplacer la tâche"); }
  };
  const toggleDone = async (it) => { if (it.kind !== "task") return; await api.patch(`/tasks/${it.id}/done`); load(weekStart); };
  const removeTask = async (it) => { try { await api.delete(`/tasks/${it.id}`); toast.success("Tâche supprimée"); load(weekStart); } catch { toast.error("Erreur de suppression"); } finally { setDelTask(null); } };
  const toggleAssignee = (id) => setForm((f) => ({ ...f, assignees: f.assignees.includes(id) ? f.assignees.filter((a) => a !== id) : [...f.assignees, id] }));
  const create = async () => {
    if (!form.title.trim()) return toast.error("Titre requis");
    try { await api.post("/tasks", { ...form, event_id: form.event_id || null }); toast.success("Tâche créée"); setOpen(false); setForm(empty); load(weekStart); }
    catch { toast.error("Erreur lors de la création"); }
  };

  const Card = ({ it }) => {
    const t = TASK_TYPES[it.type] || TASK_TYPES.autre;
    const auto = it.kind !== "task";
    return (
      <div data-testid={`card-${it.id}`}
        draggable={canManage && !auto}
        onDragStart={() => { if (canManage && !auto) dragId.current = it.id; }}
        className={`rounded-lg border border-zinc-800 border-l-4 ${t.bar} bg-zinc-900/70 p-2.5 ${it.done ? "opacity-50" : ""} ${canManage && !auto ? "cursor-grab active:cursor-grabbing" : ""}`}>
        <div className="flex items-start gap-2">
          {!auto ? (
            <button data-testid={`done-${it.id}`} onClick={() => toggleDone(it)} className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${it.done ? "border-emerald-500 bg-emerald-500 text-white" : "border-zinc-600"}`}>{it.done && <Check className="h-3 w-3" />}</button>
          ) : <Lock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-zinc-600" title="Automatique" />}
          <div className="min-w-0 flex-1">
            <p className={`truncate text-sm font-medium text-white ${it.done ? "line-through" : ""}`}>{it.title}</p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${t.badge}`}>{t.label}</span>
              {it.contact_name && <span className="text-[10px] text-zinc-500">{it.contact_name}</span>}
            </div>
            {it.location && <p className="mt-1 flex items-center gap-1 truncate text-[10px] text-zinc-500"><MapPin className="h-3 w-3" />{it.location}</p>}
            {it.assignees?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{it.assignees.map((a, i) => <span key={i} className="rounded-full bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-300">{a}</span>)}</div>}
          </div>
          {it.event_id && <button onClick={() => navigate("/app/calendrier")} className="text-zinc-600 hover:text-white"><CalIcon className="h-3.5 w-3.5" /></button>}
          {canManage && !auto && <button data-testid={`del-${it.id}`} onClick={() => setDelTask(it)} className="text-zinc-600 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-[calc(100vh-140px)] flex-col" data-testid="planning-page">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">Planning</h1>
            <p className="mt-1 text-sm text-zinc-500">Événements, chargements, livraisons et tâches</p>
          </div>
          <div className="flex items-center gap-1 rounded-full border border-zinc-800 bg-zinc-900 p-1">
            <button data-testid="week-prev" onClick={() => load(data.prev_week)} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"><ChevronLeft className="h-4 w-4" /></button>
            <button data-testid="week-today" onClick={() => load()} className="px-3 text-xs font-medium text-zinc-300 hover:text-white">Cette semaine</button>
            <button data-testid="week-next" onClick={() => load(data.next_week)} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="filter-mine" onClick={() => setMine((m) => !m)} className={`flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-medium transition-colors ${mine ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" : "border-zinc-800 text-zinc-400 hover:text-white"}`}><UserCheck className="h-4 w-4" />Mes tâches</button>
          {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-task-btn" className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Nouvelle tâche</Button></DialogTrigger>
            <DialogContent className="border-zinc-800 bg-zinc-950 text-white">
              <DialogHeader><DialogTitle>Nouvelle tâche</DialogTitle><DialogDescription className="text-zinc-500">Créez une tâche et assignez des membres de l'équipe.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-zinc-400">Titre</Label><Input data-testid="task-title" className="mt-1 border-zinc-800 bg-zinc-900" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-zinc-400">Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="task-type" className="mt-1 border-zinc-800 bg-zinc-900"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{TASK_TYPES[t].label}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="text-zinc-400">Date</Label><Input data-testid="task-date" type="date" className="mt-1 border-zinc-800 bg-zinc-900" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                </div>
                <div><Label className="text-zinc-400">Assigné à</Label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {data.members.map((m) => (
                      <button key={m.id} type="button" data-testid={`assignee-${m.id}`} onClick={() => toggleAssignee(m.id)} className={`rounded-full px-3 py-1 text-xs ${form.assignees.includes(m.id) ? "bg-[#FF6A00] text-white" : "bg-zinc-800 text-zinc-300"}`}>{m.name}</button>
                    ))}
                  </div>
                </div>
                <div><Label className="text-zinc-400">Notes</Label><Textarea rows={2} className="mt-1 border-zinc-800 bg-zinc-900" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <Button data-testid="task-submit" onClick={create} className="w-full rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">Créer la tâche</Button>
              </div>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-2 overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 md:grid-cols-4 lg:grid-cols-7" data-testid="planning-board">
        {(data.days || []).map((d) => {
          const items = ((data.columns || {})[d.date] || []).filter((it) => !mine || (it.kind === "task" && (it.assignee_ids || []).includes(data.me)));
          return (
            <div key={d.date} data-testid={`col-${d.date}`} onDragOver={(e) => canManage && e.preventDefault()} onDrop={() => moveTask(d.date)} className={`flex min-h-0 flex-col border-b border-r border-zinc-800 ${d.is_today ? "bg-[#FF6A00]/[0.04]" : ""}`}>
              <div className={`flex items-center justify-between border-b px-3 py-2.5 ${d.is_today ? "border-[#FF6A00]/30" : "border-zinc-800"}`}>
                <span className={`text-sm font-semibold capitalize ${d.is_today ? "text-[#FF6A00]" : "text-white"}`}>{d.label} {d.is_today ? <span className="ml-0.5 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#FF6A00] px-1 text-white">{d.day}</span> : d.day}</span>
                <span className="rounded-full bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400">{items.length}</span>
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto p-2 no-scrollbar">
                {items.length === 0 ? <p className="pt-6 text-center text-[11px] text-zinc-700">—</p> : items.map((it) => <Card key={it.id} it={it} />)}
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!delTask} onOpenChange={(o) => !o && setDelTask(null)}
        title="Supprimer cette tâche ?"
        description={delTask ? `« ${delTask.title} » sera définitivement supprimée. Cette action est irréversible.` : ""}
        onConfirm={() => removeTask(delTask)} testid="del-task-dialog"
      />
    </div>
  );
}
